import React, { useState, useCallback, useRef, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Button } from 'react-bootstrap';
import { toggleFileSelection, selectAllFiles, deselectAllFiles, updateMetadataComparison, removeComparisonRow, dismissEditTip } from '../redux/slices/fixMetadataSlice';
import { addTrack, resetAudioList, setAutoPlay } from '../redux/slices/musicPlayerSlice';
import { usePlayerRowSync } from '../hooks/usePlayerRowSync';
import { setLocalListContext } from '../utils/localListContext';
import { track as trackEvent } from '../utils/analytics';
import { useToast } from '../contexts/ToastContext';
import RowActions from './RowActions';
import MetadataEditModal from './MetadataEditModal';
import RekordboxUpdateModal from './RekordboxUpdateModal';
import MetadataSubTabs from './MetadataSubTabs';
import {ReactComponent as PlayIcon } from '../assets/icons/play.svg';
import {ReactComponent as PauseIcon } from '../assets/icons/pause.svg';
import {ReactComponent as StopIcon } from '../assets/icons/stop.svg';
import {ReactComponent as AudioFileIcon } from '../assets/icons/audio-file.svg';
import {ReactComponent as TipIcon } from '../assets/icons/tip.svg';
import { useDesignV2 } from '../utils/designV2';
import './MetadataComparison.scss';

// Lightweight key normalization for UI comparison (mirrors backend normalizeKey)
const KEY_TO_OPEN = {
    'Abm': '1d', 'G#m': '1d', 'Ebm': '2d', 'D#m': '2d', 'Bbm': '3d', 'A#m': '3d',
    'Fm': '4d', 'Cm': '5d', 'Gm': '6d', 'Dm': '7d', 'Am': '8d', 'Em': '9d',
    'Bm': '10d', 'F#m': '11d', 'Gbm': '11d', 'Dbm': '12d', 'C#m': '12d',
    'B': '1m', 'Cb': '1m', 'F#': '2m', 'Gb': '2m', 'Db': '3m', 'C#': '3m',
    'Ab': '4m', 'G#': '4m', 'Eb': '5m', 'D#': '5m', 'Bb': '6m', 'A#': '6m',
    'F': '7m', 'C': '8m', 'G': '9m', 'D': '10m', 'A': '11m', 'E': '12m',
};
function normalizeKeyNotation(key) {
    if (!key) return null;
    const k = key.trim()
        .replace(/\s*(minor|min)\s*$/i, 'm')
        .replace(/\s*(major|maj)\s*$/i, '')
        .trim();
    return KEY_TO_OPEN[k] || null;
}


// ---- Row rendering (module level, memoized) ----
// Analysis streams one finished file per tick (batched by FixMetadataTab).
// Rows used to be built inline in the table, so every flush re-rendered all
// 200 of them — 12 diff cells, artwork and a context menu each. Each row is
// now a memo component keyed by file path: its props are its own item
// (Immer keeps untouched rows' identity), the column widths, the date
// format, booleans derived for THIS row (playing state, its own context-menu
// anchor) and a stable `api` that reads the table's latest handlers through
// a ref — so a tick re-renders only the row that changed.

function rbBadgeTitle(item) {
    const iss = item.rekordbox.issues || {};
    const probs = [];
    if (iss.junkTitle) probs.push(`shows “${item.rekordbox.title || ''}” as the title`);
    if (iss.missingArtist) probs.push('has no artist');
    return `Rekordbox ${probs.join(' and ')} — the file's own tags are correct. Click to review the fix.`;
}

const menuTrackFor = (item) => ({
    title: item.database?.title || item.current?.title || (item.fileName || '').replace(/\.[^/.]+$/, ''),
    artist: item.database?.artist || item.current?.artist || '',
    filePath: item.filePath,
    artwork: item.database?.coverUrl || null,
    djpalId: item.database?.djpalId || null,
});

// Helper function to format dates based on user preference.
// Parses an ISO-ish date (YYYY, YYYY-MM, YYYY-MM-DD, or with / . separators)
// and re-renders it in the chosen format, degrading gracefully when the
// month/day aren't known. Kept in sync with formatReleaseDate() in
// src/backend/fixMetadata.js (used when actually writing the tag).
const formatDateValue = (dateValue, dateFormat) => {
    if (!dateValue || dateValue === '-') return dateValue;
    const s = String(dateValue).trim();
    const m = s.match(/^(\d{4})(?:[-/.](\d{1,2}))?(?:[-/.](\d{1,2}))?/);
    if (!m) return s;
    const year = m[1];
    const month = m[2] ? m[2].padStart(2, '0') : null;
    const day = m[3] ? m[3].padStart(2, '0') : null;

    switch (dateFormat) {
        case 'year':
            return year;
        case 'monthAndYear':
            return month ? `${year}-${month}` : year;
        case 'dmyDot':
            return (day && month) ? `${day}.${month}.${year}` : (month ? `${month}.${year}` : year);
        case 'dmySlash':
            return (day && month) ? `${day}/${month}/${year}` : (month ? `${month}/${year}` : year);
        case 'mdySlash':
            return (day && month) ? `${month}/${day}/${year}` : (month ? `${month}/${year}` : year);
        case 'fullDate':
        default:
            return day ? `${year}-${month}-${day}` : (month ? `${year}-${month}` : year);
    }
};

const renderCell = (current, database, field, metadataSource, dateFormat) => {
    // Apply date formatting if this is the releaseDate field
    let currentValue = current;
    let databaseValue = database;

    if (field === 'releaseDate') {
        currentValue = formatDateValue(current, dateFormat);
        databaseValue = formatDateValue(database, dateFormat);
    }

    // Normalize values for comparison (convert to strings and trim)
    const normalizedCurrent = String(currentValue || '').trim();
    const normalizedDatabase = String(databaseValue || '').trim();

    // Check if there's a database value
    const hasDatabaseValue = normalizedDatabase !== '';

    // For key field, treat equivalent keys as the same (Cmin == Cm, C#m == Dbm)
    let keysEquivalent = false;
    if (field === 'key' && normalizedCurrent && normalizedDatabase && normalizedCurrent !== normalizedDatabase) {
        const currentNorm = normalizeKeyNotation(normalizedCurrent);
        const dbNorm = normalizeKeyNotation(normalizedDatabase);
        if (currentNorm && dbNorm && currentNorm === dbNorm) keysEquivalent = true;
    }

    // Only show as changed if values are actually different
    const hasChange = normalizedCurrent !== normalizedDatabase && hasDatabaseValue && !keysEquivalent;

    // Show checkmark only if values match, there's a database value, AND data came from API (not filename parsing)
    const isMatching = (normalizedCurrent === normalizedDatabase || keysEquivalent) && hasDatabaseValue && metadataSource === 'api';

    const displayCurrent = currentValue || '-';
    const displayDatabase = databaseValue || '-';

    return (
        <div className="metadata-cell">
            <div className={`current-value ${hasChange ? 'changed' : ''}${currentValue ? '' : ' is-empty'}`}>
                {displayCurrent}
                {isMatching && <span className="match-indicator" title="Verified against database">✓</span>}
            </div>
            {hasChange && (
                <>
                    <div className="arrow">→</div>
                    <div className="new-value">{displayDatabase}</div>
                </>
            )}
        </div>
    );
};

const PENDING_FIELDS = ['artist', 'title', 'album', 'albumArtist', 'label', 'releaseDate', 'genre', 'bpm', 'key', 'track', 'composer', 'discNumber'];

// Placeholder row for a file the analyzer has not reached yet: filename and
// menu only, checkbox disabled, a pulsing skeleton where the first diff cell
// will be, the rest empty — NOT dashes, since "-" in this table means "the tag
// is empty", which is a finding. Same class hooks as the other tabs' pending
// rows so the dim + pulse styles are shared.
function PendingMetadataRow({ item, columnWidths, api, hiddenCols = '' }) {
    const hidden = hiddenCols ? new Set(hiddenCols.split(',')) : null;
    const fields = hidden ? PENDING_FIELDS.filter((f) => !hidden.has(f)) : PENDING_FIELDS;
    return (
        <tr className="pending" onContextMenu={(e) => api.rowContextMenu(item, e)}>
            <td className="artwork-col" style={{ width: `${columnWidths.artwork}px` }}>
                <div className="metadata-cell">
                    <div className="artwork-placeholder" />
                </div>
            </td>
            <td className="checkbox-col" style={{ width: `${columnWidths.checkbox}px` }} onClick={(e) => e.stopPropagation()}>
                <input type="checkbox" checked={false} disabled readOnly />
            </td>
            <td className="file-col" style={{ width: `${columnWidths.fileName}px` }} title={item.filePath}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '4px', minWidth: 0 }}>
                    <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.fileName}</span>
                    <span onClick={(e) => e.stopPropagation()}>
                        <RowActions
                            track={menuTrackFor(item)}
                            onAddToQueue={() => api.addToQueue(item)}
                            onRemoveFromLibrary={() => api.removeFromList(item.filePath)}
                            removeLabel="Remove from list"
                            openAtPoint={null}
                            desktopLike
                            analyticsFrom="fix_metadata"
                        />
                    </span>
                </div>
            </td>
            {fields.map((f, i) => (
                <td key={f} style={{ width: `${columnWidths[f]}px` }}>
                    {i === 0 && <span className="status-badge pending-skeleton" title="Waiting to be analyzed">{' '}</span>}
                </td>
            ))}
        </tr>
    );
}

// DESIGN V2 hides columns that are empty for every row; `hiddenCols` is a
// comma-joined key (stable string → cheap memo compare) of the field keys
// to skip. Empty under the old design.
const MetadataRow = React.memo(function MetadataRow({ item, columnWidths, dateFormat, isCurrent, playing, ctxAt, api, hiddenCols = '' }) {
    const hidden = hiddenCols ? new Set(hiddenCols.split(',')) : null;
    const cell = (field) => (hidden && hidden.has(field) ? null : (
        <td key={field} style={{ width: `${columnWidths[field]}px` }}>
            {renderCell(item.current[field], item.database[field], field, item.metadataSource, dateFormat)}
        </td>
    ));
    if (item.pending) return <PendingMetadataRow item={item} columnWidths={columnWidths} api={api} hiddenCols={hiddenCols} />;
    const artworkCell = (artwork, filePath, coverUrl) => {
        // Generate image URL or use dummy artwork
        let imageUrl;
        if (artwork && artwork.dataUrl) {
            // Thumbnail the analyzer already encoded once (fixMetadata.js
            // artworkThumbnail) — no per-render byte shuffling.
            imageUrl = artwork.dataUrl;
        } else if (artwork && artwork.data) {
            // Convert buffer to base64 for display
            const base64 = btoa(
                new Uint8Array(artwork.data).reduce(
                    (data, byte) => data + String.fromCharCode(byte),
                    ''
                )
            );
            imageUrl = `data:${artwork.format || 'image/jpeg'};base64,${base64}`;
        } else if (coverUrl) {
            // File has no embedded art but Mixpedia gave us a cover — show it.
            imageUrl = coverUrl;
        } else {
            // Use a simple SVG as dummy artwork (musical note icon)
            imageUrl = `data:image/svg+xml;base64,${btoa(`
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100">
                    <rect width="100" height="100" fill="#333"/>
                    <path d="M70 20 L70 60 C70 68 64 75 55 75 C46 75 40 68 40 60 C40 52 46 45 55 45 C58 45 61 46 64 48 L64 30 L45 34 L45 74 C45 82 39 89 30 89 C21 89 15 82 15 74 C15 66 21 59 30 59 C33 59 36 60 39 62 L39 20 L70 20 Z" fill="#666"/>
                </svg>
            `)}`;
        }

        // Which icon to show — derived for THIS row by the parent (isCurrent / playing)
        const shouldShowPause = isCurrent && playing;
        const shouldShowStop = isCurrent && !playing;

        let IconComponent, handleIconClick;

        if (shouldShowPause) {
            IconComponent = PauseIcon;
            handleIconClick = (e) => {
                e.stopPropagation();
                api.pauseClick();
            };
        } else if (shouldShowStop) {
            IconComponent = StopIcon;
            handleIconClick = (e) => {
                e.stopPropagation();
                api.stopClick();
            };
        } else {
            IconComponent = PlayIcon;
            handleIconClick = (e) => {
                e.stopPropagation();
                api.playClick(item.filePath, item.current);
            };
        }

        return (
            <div className="metadata-cell">
                {/* Matches CoverPlayButton used across the other tabs: 24px art
                    with the play/pause/stop triangle overlaid directly (white +
                    shadow), no dark circle. */}
                <div style={{ position: 'relative', width: '24px', height: '24px' }}>
                    <img
                        src={imageUrl}
                        alt="Album artwork"
                        style={{
                            width: '24px',
                            height: '24px',
                            objectFit: 'cover',
                            borderRadius: '3px'
                        }}
                    />
                    <div
                        onClick={handleIconClick}
                        style={{
                            position: 'absolute',
                            inset: 0,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            cursor: 'pointer'
                        }}
                    >
                        <IconComponent
                            style={{
                                width: '12px',
                                height: '12px',
                                fill: 'white',
                                filter: 'drop-shadow(0 0 2px rgba(0, 0, 0, 0.85))'
                            }}
                        />
                    </div>
                </div>
            </div>
        );
    
    };

    return (
        <tr
            className={item.selected ? 'selected' : ''}
            onClick={() => api.rowClick(item)}
            onContextMenu={(e) => api.rowContextMenu(item, e)}
            style={{ cursor: 'pointer' }}
        >
            <td className="artwork-col" style={{ width: `${columnWidths.artwork}px` }}>
                {artworkCell(item.current.artwork, item.filePath, item.database?.coverUrl)}
            </td>
            <td className="checkbox-col" style={{ width: `${columnWidths.checkbox}px` }} onClick={(e) => e.stopPropagation()}>
                <input
                    type="checkbox"
                    checked={item.selected}
                    onChange={(e) => api.toggle(item.filePath, e)}
                />
            </td>
            <td className="file-col" style={{ width: `${columnWidths.fileName}px` }} title={item.filePath}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '4px', minWidth: 0 }}>
                    <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.fileName}</span>
                    {item.rekordbox?.stale && (
                        <span
                            className="rb-stale-badge"
                            role="button"
                            title={rbBadgeTitle(item)}
                            onClick={(e) => { e.stopPropagation(); api.openRbConfirm(); }}
                        >RB</span>
                    )}
                    <span onClick={(e) => e.stopPropagation()}>
                        <RowActions
                            track={menuTrackFor(item)}
                            onAddToQueue={() => api.addToQueue(item)}
                            onRemoveFromLibrary={() => api.removeFromList(item.filePath)}
                            removeLabel="Remove from list"
                            openAtPoint={ctxAt}
                            desktopLike
                            analyticsFrom="fix_metadata"
                        />
                    </span>
                </div>
            </td>
            {['artist', 'title', 'album', 'albumArtist', 'label', 'releaseDate', 'genre', 'bpm', 'key', 'track', 'composer', 'discNumber'].map(cell)}
        </tr>
    );
});

const MetadataComparison = ({ activeSubTab, onSubTabChange }) => {
    const dispatch = useDispatch();
    // DESIGN V2: selection + Select all live in the table header row, the
    // toolbar is gone (tabs moved into the tool header), and columns that are
    // empty for every row are hidden.
    const v2 = useDesignV2();
    const { metadataComparisons, backupBeforeWrite, enabledFields, dateFormat, hideEditTip, isProcessing } = useSelector((state) => state.fixMetadata);

    const { concurrencyLevel } = useSelector((state) => state.settings);

    // Local state for playback control (like FixMyTracksTab)
    const [currentlyPlayingPath, setCurrentlyPlayingPath] = useState(null);
    const [isPlaying, setIsPlaying] = useState(false);
    // Reconcile the row highlight with the bottom player: pause/stop flips the
    // icon back, and list auto-advance moves the highlight to the next row.
    usePlayerRowSync(currentlyPlayingPath, setIsPlaying, setCurrentlyPlayingPath);

    const [editingFile, setEditingFile] = useState(null);
    const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
    const toast = useToast();
    // "Update Rekordbox" in-flight guard (the python updater takes a moment).
    const [rbSyncing, setRbSyncing] = useState(false);
    // Confirmation modal for the Rekordbox update + whether Rekordbox is open
    // (shown up front so "applies when you close it" isn't a surprise toast).
    const [rbConfirmOpen, setRbConfirmOpen] = useState(false);
    const [rbAppOpen, setRbAppOpen] = useState(false);
    // Callout dismissal lasts for the current results only — a fresh analyze
    // re-raises the finding.
    const [rbCalloutDismissed, setRbCalloutDismissed] = useState(false);
    React.useEffect(() => {
        if (isProcessing) setRbCalloutDismissed(false);
    }, [isProcessing]);

    // Right-click opens the row's shared TrackContextMenu at the cursor —
    // identical options to the ⋯ button (same parity contract as every other
    // track-list tab).
    const [rowCtx, setRowCtx] = useState(null); // { key, x, y, nonce }
    // Latest handlers for the memoized rows (see MetadataRow). The row's
    // `api` prop never changes identity; each method reads the current
    // closure through this ref, so rows don't re-render on every table render.
    const rowHandlersRef = React.useRef({});
    const rowApi = React.useMemo(() => ({
        rowClick: (item) => rowHandlersRef.current.rowClick(item),
        rowContextMenu: (item, e) => rowHandlersRef.current.rowContextMenu(item, e),
        toggle: (filePath, e) => rowHandlersRef.current.toggle(filePath, e),
        openRbConfirm: () => rowHandlersRef.current.openRbConfirm(),
        addToQueue: (item) => rowHandlersRef.current.addToQueue(item),
        removeFromList: (filePath) => rowHandlersRef.current.removeFromList(filePath),
        playClick: (filePath, current) => rowHandlersRef.current.playClick(filePath, current),
        pauseClick: () => rowHandlersRef.current.pauseClick(),
        stopClick: () => rowHandlersRef.current.stopClick(),
    }), []);

    const ctxNonceRef = React.useRef(0);

    // Column resizing state - reduced widths for Excel-like compact view
    // DESIGN V2: the table fills the page (fixed layout scales these), so the
    // shares matter — names get the room, BPM / key / track numbers stay narrow.
    const [columnWidths, setColumnWidths] = useState(v2 ? {
        artwork: 40, checkbox: 35, fileName: 200, artist: 170, title: 200, album: 140, albumArtist: 130,
        label: 110, releaseDate: 100, genre: 110, bpm: 56, key: 52, track: 50, composer: 110, discNumber: 50,
    } : {
        artwork: 40,
        checkbox: 35,
        fileName: 150,
        artist: 120,
        title: 120,
        album: 120,
        albumArtist: 100,
        label: 90,
        releaseDate: 120, // Increased for full width
        genre: 120, // Increased for full width
        bpm: 80, // Increased for full width
        key: 80, // Increased for full width
        track: 50,
        composer: 90,
        discNumber: 50
    });
    const [resizingColumn, setResizingColumn] = useState(null);
    const [startX, setStartX] = useState(null);
    const [startWidth, setStartWidth] = useState(null);

    // Column resizing event handlers
    const handleMouseMove = useCallback((e) => {
        if (resizingColumn && startX !== null && startWidth !== null) {
            e.preventDefault();
            const diff = e.clientX - startX;
            const newWidth = Math.max(50, startWidth + diff); // Minimum width of 50px
            setColumnWidths(prev => ({
                ...prev,
                [resizingColumn]: newWidth
            }));
            document.body.style.cursor = 'col-resize';
            document.body.style.userSelect = 'none';
        }
    }, [resizingColumn, startX, startWidth]);

    const handleMouseUp = useCallback(() => {
        if (resizingColumn) {
            setResizingColumn(null);
            setStartX(null);
            setStartWidth(null);
            document.body.style.cursor = '';
            document.body.style.userSelect = '';
        }
    }, [resizingColumn]);

    // Add/remove mouse event listeners
    React.useEffect(() => {
        if (resizingColumn) {
            document.addEventListener('mousemove', handleMouseMove);
            document.addEventListener('mouseup', handleMouseUp);
            return () => {
                document.removeEventListener('mousemove', handleMouseMove);
                document.removeEventListener('mouseup', handleMouseUp);
            };
        }
    }, [resizingColumn, handleMouseMove, handleMouseUp]);

    // Which field columns carry a value in ANY row (v2 hides the rest). A
    // joined key so the memoized rows compare one string, not a Set.
    const hiddenColsKey = React.useMemo(() => {
        if (!v2) return '';
        const fields = ['artist', 'title', 'album', 'albumArtist', 'label', 'releaseDate', 'genre', 'bpm', 'key', 'track', 'composer', 'discNumber'];
        const rows = metadataComparisons || [];
        if (rows.some((r) => r.pending)) return '';   // shape unknown until results land
        return fields.filter((f) => !rows.some((r) => String(r.current?.[f] ?? '').trim() || String(r.database?.[f] ?? '').trim())).join(',');
    }, [v2, metadataComparisons]);
    const hiddenColSet = React.useMemo(() => new Set(hiddenColsKey ? hiddenColsKey.split(',') : []), [hiddenColsKey]);
    // DESIGN V2: the shown columns are scaled down to fit the table's width (fixed
    // layout + px widths otherwise ran past the edge on a ~1220px window and cut
    // the last columns off). The artwork and checkbox columns keep their size.
    // A callback ref: the table mounts only once there are rows, after the first effects ran.
    const [tableWrapWidth, setTableWrapWidth] = useState(0);
    const tableWrapObserver = useRef(null);
    const tableWrapRef = useCallback((node) => {
        tableWrapObserver.current?.disconnect();
        tableWrapObserver.current = null;
        if (!v2 || !node || typeof ResizeObserver === 'undefined') return;
        const ro = new ResizeObserver(([entry]) => setTableWrapWidth(Math.floor(entry.contentRect.width)));
        ro.observe(node);
        tableWrapObserver.current = ro;
    }, [v2]);
    const fitWidths = useMemo(() => {
        if (!v2 || !tableWrapWidth) return columnWidths;
        // Short values (BPM, key, numbers) keep their width; text columns share the
        // rest down to a floor, below which the table scrolls sideways instead.
        const fixed = ['artwork', 'checkbox', 'bpm', 'key', 'track', 'discNumber'];
        const TEXT_FLOOR = 96;
        const visible = (k) => !hiddenColSet.has(k);
        const shown = Object.keys(columnWidths).filter((k) => !fixed.includes(k) && visible(k));
        const room = tableWrapWidth - fixed.filter(visible).reduce((n, k) => n + columnWidths[k], 0);
        const sum = shown.reduce((n, k) => n + columnWidths[k], 0);
        if (sum <= room || room <= 0) return columnWidths;
        const out = { ...columnWidths };
        shown.forEach((k) => { out[k] = Math.max(TEXT_FLOOR, Math.floor((columnWidths[k] * room) / sum)); });
        return out;
    }, [v2, tableWrapWidth, columnWidths, hiddenColSet]);

    if (!metadataComparisons || metadataComparisons.length === 0) {
        return (
            <>
                {!v2 && <div className="file-summary-container">
                    <div className="changes-container log-entry">
                        <span>Select a folder and click "Analyze" to compare metadata</span>
                    </div>
                </div>}
                <div className="file-list-container">
                    {v2 && !hideEditTip && (
                        <div className="v2-tip">
                            <TipIcon className="tip-icon" />
                            <span>Analyze compares each file's tags with Mixpedia. Click any row afterwards to edit its metadata by hand.</span>
                        </div>
                    )}
                    <div className="no-files-message">
                        <AudioFileIcon className="no-files-icon" />
                        <span>{v2 ? 'Nothing analyzed yet' : 'No audio files were found in folder'}</span>
                    </div>
                </div>
            </>
        );
    }

    // Sort function
    const handleSort = (key) => {
        let direction = 'asc';
        if (sortConfig.key === key && sortConfig.direction === 'asc') {
            direction = 'desc';
        }
        setSortConfig({ key, direction });
    };

    const getFilteredData = () => metadataComparisons;

    // Get sorted data
    const getSortedData = () => {
        const filteredData = getFilteredData();

        if (!sortConfig.key) return filteredData;

        const sorted = [...filteredData].sort((a, b) => {
            let aValue, bValue;

            if (sortConfig.key === 'fileName') {
                aValue = a.fileName;
                bValue = b.fileName;
            } else {
                // For metadata fields, use current value
                aValue = a.current[sortConfig.key] || '';
                bValue = b.current[sortConfig.key] || '';
            }

            // Convert to string for comparison
            aValue = String(aValue).toLowerCase();
            bValue = String(bValue).toLowerCase();

            if (aValue < bValue) {
                return sortConfig.direction === 'asc' ? -1 : 1;
            }
            if (aValue > bValue) {
                return sortConfig.direction === 'asc' ? 1 : -1;
            }
            return 0;
        });

        return sorted;
    };

    const sortedData = getSortedData();
    const selectedCount = metadataComparisons.filter(item => item.selected).length;
    const filesWithChanges = metadataComparisons.filter(item => item.hasChanges);

    const handleSelectAll = () => {
        dispatch(selectAllFiles());
    };

    const handleDeselectAll = () => {
        dispatch(deselectAllFiles());
    };

    const handleToggle = (filePath, event) => {
        event.stopPropagation();
        dispatch(toggleFileSelection(filePath));
    };

    const handleRowClick = (item) => {
        if (item.pending) return; // nothing to edit yet
        setEditingFile(item);
    };

    const handleSaveEdit = async (filePath, editedData) => {
        // Immediately write tags to the file
        const fileItem = metadataComparisons.find(item => item.filePath === filePath);
        if (fileItem) {
            // Write this single file immediately
            await window.electron.writeMetadata(
                {
                    files: [{
                        ...fileItem,
                        database: editedData
                    }],
                    backupBeforeWrite: false,
                    // Manual edit: the user typed these values explicitly, so write
                    // exactly what they entered — force over existing values and keys.
                    overwriteExisting: true,
                    overrideExistingKey: true,
                    concurrencyLevel: 1
                },
                () => {} // Empty progress callback for single file
            );

            // Update the comparison table to reflect the written tags as current values
            dispatch(updateMetadataComparison({
                filePath,
                data: {
                    current: editedData,
                    database: editedData
                }
            }));
        }
    };

    // Tracks whose FILE tags are clean but whose Rekordbox DB row has diagnosed
    // problems (junkTitle: title = raw filename; missingArtist: no artist).
    // The backend flagged them during analyze (comparison.rekordbox.issues);
    // the fix mirrors the file tags into Rekordbox's DB without touching files,
    // restricted per row to the diagnosed fields so curated values survive.
    const rbStaleItems = metadataComparisons.filter((item) => item.rekordbox?.stale);
    const rbJunkTitleCount = rbStaleItems.filter((i) => i.rekordbox.issues?.junkTitle).length;
    const rbMissingArtistCount = rbStaleItems.filter((i) => i.rekordbox.issues?.missingArtist).length;

    // The callout sentence is generated from the findings so it never claims a
    // problem a flagged track doesn't have.
    const rbCalloutText = (() => {
        const n = rbStaleItems.length;
        const jt = rbJunkTitleCount;
        const ma = rbMissingArtistCount;
        const tail = ' The files themselves are tagged correctly — MixGoat can fix Rekordbox’s library from their tags.';
        if (jt === n && ma === n) {
            return `Rekordbox is showing the raw filename for ${n} of these track${n !== 1 ? 's' : ''} (no artist).${tail}`;
        }
        if (jt && ma) {
            return `Rekordbox has outdated info for ${n} of these tracks — raw filename as the title (${jt}), missing artist (${ma}).${tail}`;
        }
        if (jt) {
            return `Rekordbox is showing the raw filename as the title for ${jt} of these track${jt !== 1 ? 's' : ''}.${tail}`;
        }
        return `Rekordbox is missing the artist for ${ma} of these track${ma !== 1 ? 's' : ''}.${tail}`;
    })();

    // Row-badge tooltip naming this track's actual problems.
    // Button click → confirmation modal (an external-DB write should never run
    // without the user seeing exactly what changes). The Rekordbox-running
    // check is async; the note just appears when the answer arrives.
    const handleOpenRbConfirm = () => {
        setRbAppOpen(false);
        setRbConfirmOpen(true);
        window.electron.rekordboxIsRunning?.().then((open) => setRbAppOpen(!!open)).catch(() => {});
    };

    const handleSyncRekordbox = async (filePaths) => {
        const wanted = new Set(filePaths || []);
        const stale = metadataComparisons.filter((item) => item.rekordbox?.stale && wanted.has(item.filePath));
        if (stale.length === 0 || rbSyncing) return;
        setRbSyncing(true);
        try {
            trackEvent('rekordbox_tags_sync', {
                track_count: stale.length,
                from: 'fix_metadata',
                junk_title_count: stale.filter((i) => i.rekordbox.issues?.junkTitle).length,
                missing_artist_count: stale.filter((i) => i.rekordbox.issues?.missingArtist).length,
            });
            // Both issues → the row is pristine import junk: sync every file tag.
            // One issue → part of the row is curated: write ONLY the bad field.
            const res = await window.electron.rekordboxSyncFileTags(stale.map((item) => {
                const iss = item.rekordbox.issues || {};
                if (iss.junkTitle && iss.missingArtist) return { path: item.filePath };
                return {
                    path: item.filePath,
                    fields: [...(iss.junkTitle ? ['title'] : []), ...(iss.missingArtist ? ['artist'] : [])],
                };
            }));
            if (res?.queued) {
                toast.warning('Rekordbox is open — its library will be updated automatically when you close it.');
            } else if (res?.success) {
                const n = res.tagsSynced ?? stale.length;
                toast.success(`Rekordbox updated for ${n} track${n !== 1 ? 's' : ''}`);
            } else {
                toast.error(res?.error ? `Rekordbox update failed: ${res.error}` : 'Rekordbox update failed');
            }
            if (res?.success) {
                // Reflect what was (or will be, if queued) written — only the
                // diagnosed fields changed — and clear the flags so the callout
                // and badges don't invite a double run.
                for (const item of stale) {
                    const iss = item.rekordbox.issues || {};
                    dispatch(updateMetadataComparison({
                        filePath: item.filePath,
                        data: {
                            rekordbox: {
                                title: iss.junkTitle ? item.current.title : item.rekordbox.title,
                                artist: iss.missingArtist ? item.current.artist : item.rekordbox.artist,
                                stale: false,
                                issues: { junkTitle: false, missingArtist: false, stale: false },
                            },
                        },
                    }));
                }
                setRbConfirmOpen(false);
            }
        } finally {
            setRbSyncing(false);
        }
    };

    // Register the visible (sorted) rows as the player's list context so
    // playback continues down the list when a track ends — auto-radio only
    // takes over after the LAST row. Analyzed identity beats the raw tags;
    // djpalId lets the radio hand-off seed catalog-known tracks first.
    const registerListContext = (filePath) => {
        setLocalListContext(
            dispatch,
            sortedData.map((it) => it.filePath),
            filePath,
            (p) => {
                const it = metadataComparisons.find((c) => c.filePath === p);
                const t = it ? menuTrackFor(it) : null;
                return t ? { title: t.title, artist: t.artist, cover: t.artwork || '', djpalId: t.djpalId || null } : null;
            }
        );
    };

    const handlePlayClick = async (filePath, metadata) => {
        try {
            // If clicking on currently playing track that's paused, resume it
            if (currentlyPlayingPath === filePath && !isPlaying) {
                if (window.musicPlayerInstance) {
                    window.musicPlayerInstance.play();
                }
                // Re-assert the list context (see FixMyTracksTab): resuming
                // must restore play-through-the-list, not solo play.
                registerListContext(filePath);
                setIsPlaying(true);
                dispatch(setAutoPlay(true));
                return;
            }

            // Extract artist and title from metadata
            const artist = metadata.artist || 'Unknown Artist';
            const title = metadata.title || metadata.fileName || 'Unknown Track';

            // Load and play new track
            const musicSrc = await window.electron.getAudioUrl(filePath);

            // Try to get album art
            let albumArtUrl = '';
            try {
                albumArtUrl = await window.electron.getAlbumArtUrl(filePath);
            } catch (err) {
            }

            const newTrack = {
                name: title,
                singer: artist,
                cover: albumArtUrl,
                musicSrc
            };

            // Reset audio list and add only the new track
            dispatch(resetAudioList());
            dispatch(addTrack(newTrack));
            registerListContext(filePath);
            dispatch(setAutoPlay(true));

            setCurrentlyPlayingPath(filePath);
            setIsPlaying(true);
        } catch (error) {
            console.error('Error playing track:', error);
        }
    };

    const handlePauseClick = () => {
        // Use the audio instance directly to pause
        if (window.musicPlayerInstance) {
            window.musicPlayerInstance.pause();
        }
        setIsPlaying(false);
        dispatch(setAutoPlay(false));
    };

    // ---- Row menu (⋯ + right-click), same shared menu as every track tab ----
    // Prefer the analyzed (database/Mixpedia) identity over the raw tags: the
    // menu copies/searches the real name, and a Like carries cover + catalog id.
    const handleAddToQueue = async (item) => {
        try {
            const t = menuTrackFor(item);
            const musicSrc = await window.electron.getAudioUrl(item.filePath);
            let albumArtUrl = '';
            try {
                albumArtUrl = await window.electron.getAlbumArtUrl(item.filePath);
            } catch (err) {
            }
            dispatch(addTrack({
                name: t.title || 'Unknown Track',
                singer: t.artist || 'Unknown Artist',
                cover: t.artwork || albumArtUrl,
                musicSrc,
            }));
        } catch (error) {
            console.error('Error adding track to queue:', error);
        }
    };

    const removeRowFromList = (filePath) => {
        dispatch(removeComparisonRow(filePath));
        trackEvent('tracks_removed_from_list', { tab: 'fix_metadata', count: 1, via: 'menu' });
    };

    const handleRowContextMenu = (item, e) => {
        e.preventDefault();
        setRowCtx({ key: item.filePath, x: e.clientX, y: e.clientY, nonce: ++ctxNonceRef.current });
    };

    const handleStopClick = () => {
        // Use the audio instance directly to pause first
        if (window.musicPlayerInstance) {
            window.musicPlayerInstance.pause();
            window.musicPlayerInstance.currentTime = 0;
        }
        setIsPlaying(false);
        setCurrentlyPlayingPath(null);
        dispatch(setAutoPlay(false));
        dispatch(resetAudioList());
    };

    // Column resizing handlers
    const handleMouseDown = (e, columnKey) => {
        e.preventDefault();
        e.stopPropagation();
        setResizingColumn(columnKey);
        setStartX(e.clientX);
        setStartWidth(columnWidths[columnKey]);
    };

    const renderSortableHeader = (label, key) => {
        if (hiddenColSet.has(key)) return null;
        const isSorted = sortConfig.key === key;
        const direction = isSorted ? sortConfig.direction : null;

        return (
            <th
                onClick={() => handleSort(key)}
                style={{
                    cursor: 'pointer',
                    userSelect: 'none',
                    width: `${fitWidths[key]}px`,
                    position: 'relative'
                }}
            >
                {label}
                {v2 && key === 'fileName' && (
                    <span className="v2-th-note" onClick={(e) => e.stopPropagation()}>· {selectedCount} of {metadataComparisons.length} selected</span>
                )}
                {isSorted && (
                    <span style={{ marginLeft: '5px', fontSize: '0.7em' }}>
                        {direction === 'asc' ? '▲' : '▼'}
                    </span>
                )}
                <div
                    className="column-resizer"
                    onMouseDown={(e) => handleMouseDown(e, key)}
                    onClick={(e) => e.stopPropagation()}
                />
            </th>
        );
    };

    rowHandlersRef.current = {
        rowClick: handleRowClick,
        rowContextMenu: handleRowContextMenu,
        toggle: handleToggle,
        openRbConfirm: handleOpenRbConfirm,
        addToQueue: handleAddToQueue,
        removeFromList: removeRowFromList,
        playClick: handlePlayClick,
        pauseClick: handlePauseClick,
        stopClick: handleStopClick,
    };

    return (
        <>
            <div className="file-list-container">
                {/* Single toolbar row: sub-tab switcher, the (dismissible) tip,
                    and the selection summary + bulk buttons. Replaces the old
                    separate two-row summary card, which mostly held air. */}
                {!v2 && <div className="metadata-toolbar">
                    {activeSubTab && onSubTabChange && <MetadataSubTabs activeSubTab={activeSubTab} onSubTabChange={onSubTabChange} />}
                    {!hideEditTip && (
                        <span className="metadata-toolbar-tip">
                            <TipIcon className="tip-icon" />
                            <span className="tip-text">Tip: Click any row to manually edit metadata</span>
                            <button
                                className="tip-close-button"
                                aria-label="Dismiss tip"
                                onClick={() => dispatch(dismissEditTip())}
                            >×</button>
                        </span>
                    )}
                    <div className="metadata-toolbar-selection">
                        <span className="metadata-selection-count">{selectedCount} of {metadataComparisons.length} selected</span>
                        <div className="button-group">
                            <Button variant="outline-secondary" size="sm" onClick={handleSelectAll}>
                                Select All
                            </Button>
                            <Button variant="outline-secondary" size="sm" onClick={handleDeselectAll}>
                                Deselect All
                            </Button>
                        </div>
                    </div>
                </div>}
                {/* Analyze finding, not an action: Rekordbox's OWN library shows
                    filename junk for these tracks while the files are tagged
                    correctly. "Review fixes…" opens the confirmation — nothing
                    is written from here. */}
                {rbStaleItems.length > 0 && !rbCalloutDismissed && (
                    <div className="rb-callout">
                        <span className="rb-glyph rb-callout-icon" aria-hidden="true" />
                        <span className="rb-callout-text">{rbCalloutText}</span>
                        <Button variant="outline-primary" size="sm" disabled={rbSyncing} onClick={handleOpenRbConfirm}>
                            Review fixes…
                        </Button>
                        <button className="rb-callout-dismiss" aria-label="Dismiss" onClick={() => setRbCalloutDismissed(true)}>×</button>
                    </div>
                )}
                <div className="metadata-comparison">
                    <div className="comparison-table-container" ref={tableWrapRef}>
                        <table className="comparison-table">
                            <thead>
                                <tr>
                                    <th
                                        className="artwork-col"
                                        style={{ width: `${fitWidths.artwork}px`, position: 'relative' }}
                                    >
                                        <div
                                            className="column-resizer"
                                            onMouseDown={(e) => handleMouseDown(e, 'artwork')}
                                        />
                                    </th>
                                    <th
                                        className="checkbox-col"
                                        style={{ width: `${fitWidths.checkbox}px`, position: 'relative' }}
                                    >
                                        {v2 && (
                                            <input
                                                type="checkbox"
                                                title={selectedCount === metadataComparisons.length ? 'Deselect all' : 'Select all'}
                                                aria-label="Select all"
                                                checked={selectedCount > 0 && selectedCount === metadataComparisons.length}
                                                ref={(el) => { if (el) el.indeterminate = selectedCount > 0 && selectedCount < metadataComparisons.length; }}
                                                onChange={() => (selectedCount === metadataComparisons.length ? handleDeselectAll() : handleSelectAll())}
                                            />
                                        )}
                                        <div
                                            className="column-resizer"
                                            onMouseDown={(e) => handleMouseDown(e, 'checkbox')}
                                        />
                                    </th>
                                    {renderSortableHeader('File', 'fileName')}
                                    {renderSortableHeader('Artist', 'artist')}
                                    {renderSortableHeader('Title', 'title')}
                                    {renderSortableHeader('Album', 'album')}
                                    {renderSortableHeader('Album Artist', 'albumArtist')}
                                    {renderSortableHeader('Label', 'label')}
                                    {renderSortableHeader('Release Date', 'releaseDate')}
                                    {renderSortableHeader('Genre', 'genre')}
                                    {renderSortableHeader('BPM', 'bpm')}
                                    {renderSortableHeader('Key', 'key')}
                                    {renderSortableHeader('Track', 'track')}
                                    {renderSortableHeader('Composer', 'composer')}
                                    {renderSortableHeader('Disc #', 'discNumber')}
                                </tr>
                            </thead>
                            <tbody>
                                {sortedData.map((item) => {
                                    const isCurrent = currentlyPlayingPath === item.filePath;
                                    return (
                                        <MetadataRow
                                            key={item.filePath}
                                            item={item}
                                            columnWidths={fitWidths}
                                            dateFormat={dateFormat}
                                            isCurrent={isCurrent}
                                            playing={isCurrent && isPlaying}
                                            ctxAt={rowCtx && rowCtx.key === item.filePath ? rowCtx : null}
                                            api={rowApi}
                                            hiddenCols={hiddenColsKey}
                                        />
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
            <MetadataEditModal
                show={editingFile !== null}
                file={editingFile}
                onClose={() => setEditingFile(null)}
                onSave={handleSaveEdit}
            />
            <RekordboxUpdateModal
                show={rbConfirmOpen}
                items={rbStaleItems.map((item) => ({
                    filePath: item.filePath,
                    fileName: item.fileName,
                    rbTitle: item.rekordbox.title,
                    rbArtist: item.rekordbox.artist,
                    newArtist: item.current.artist,
                    newTitle: item.current.title,
                    issues: item.rekordbox.issues,
                }))}
                syncing={rbSyncing}
                rekordboxOpen={rbAppOpen}
                onClose={() => setRbConfirmOpen(false)}
                onConfirm={handleSyncRekordbox}
            />
        </>
    );
};

export default MetadataComparison;
